// Boiling point vs. pressure via the Antoine equation:
//   log10(P) = A − B / (C + T)      P in mmHg, T in °C
// Rearranged for boiling temperature at a given pressure:
//   T = B / (A − log10 P) − C
// Coefficients are the classic CRC/NIST values (mmHg, °C) for each solvent.

export interface Solvent {
  id: string;
  name: string;
  a: number;
  b: number;
  c: number;
}

export const SOLVENTS: Solvent[] = [
  { id: "water", name: "Water", a: 8.07131, b: 1730.63, c: 233.426 },
  { id: "ethanol", name: "Ethanol", a: 8.20417, b: 1642.89, c: 230.3 },
  { id: "methanol", name: "Methanol", a: 8.08097, b: 1582.271, c: 239.726 },
  { id: "acetone", name: "Acetone", a: 7.11714, b: 1210.595, c: 229.664 },
  {
    id: "acetonitrile",
    name: "Acetonitrile",
    a: 7.07354,
    b: 1279.2,
    c: 224.01,
  },
  {
    id: "isopropanol",
    name: "Isopropanol",
    a: 8.87829,
    b: 2010.33,
    c: 252.636,
  },
  {
    id: "ethyl-acetate",
    name: "Ethyl acetate",
    a: 7.10179,
    b: 1244.95,
    c: 217.88,
  },
  { id: "toluene", name: "Toluene", a: 6.95464, b: 1344.8, c: 219.48 },
  { id: "benzene", name: "Benzene", a: 6.90565, b: 1211.033, c: 220.79 },
  { id: "chloroform", name: "Chloroform", a: 6.90328, b: 1163.03, c: 227.4 },
  { id: "dcm", name: "Dichloromethane", a: 7.409, b: 1325.9, c: 252.6 },
  {
    id: "diethyl-ether",
    name: "Diethyl ether",
    a: 6.92032,
    b: 1064.07,
    c: 228.8,
  },
  { id: "hexane", name: "n-Hexane", a: 6.87601, b: 1171.17, c: 224.41 },
];

/** Boiling temperature (°C) of a solvent at a given pressure (mmHg). */
export function boilingPointAt(
  solvent: Solvent,
  pressureMmHg: number,
): number | null {
  if (!Number.isFinite(pressureMmHg) || pressureMmHg <= 0) return null;
  const denom = solvent.a - Math.log10(pressureMmHg);
  if (denom <= 0) return null;
  return solvent.b / denom - solvent.c;
}

/** Vapor pressure (mmHg) of a solvent at a given temperature (°C). */
export function vaporPressureAt(
  solvent: Solvent,
  tempC: number,
): number | null {
  if (!Number.isFinite(tempC)) return null;
  const denom = solvent.c + tempC;
  if (denom <= 0) return null;
  return 10 ** (solvent.a - solvent.b / denom);
}

export const MMHG_PER_MBAR = 0.7500616;
